'use client';

import { Avatar, Badge, ListItemAvatar, ListItemButton, ListItemText, Tooltip, Typography } from '@mui/material';
import { useParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import NotificationsPanel from '@/app/components/NotificationsPanel';
import { useUserContext } from '@/app/context/UserContext';
import { checkDisableNav } from '@/app/utils/check-disable-nav.util';
import { pathToDisableTopNav } from '@/app/utils/constants';
import { TbBell, TbBriefcase2, TbBuilding, TbCoins, TbCreditCard, TbGift, TbHeartRateMonitor, TbHelpCircle, TbSettings, TbUserPlus, TbUsers } from 'react-icons/tb';
import { LuBuilding2, LuFileText } from 'react-icons/lu';
import { UserRole } from '@/constants/enums';
import { DEFAULT_PRIMARY_ROLE_PRIORITY, getPrimaryAppRole } from '@/utils/app-roles.util';
import { MdOutlineChecklistRtl, MdOutlineInsights } from 'react-icons/md';
import clsx from 'clsx';
import Link from 'next/link';
import { motion } from 'motion/react';
import { useCompanyContext } from '@/app/context/CompanyContext';
import { RxDashboard } from 'react-icons/rx';
import { AppLayoutLogo } from './AppLayout.logo';
import { SIDEBAR_CSS_TRANSITION, SIDEBAR_CSS_TRANSITION_IMPORTANT, SIDEBAR_MOTION_TRANSITION } from './AppLayout.motion';
import { AppLayoutUserMenu } from './AppLayout.usermenu';
import { AppLayoutUsageBanner } from './AppLayout.usage-banner';
import { FiChevronRight } from 'react-icons/fi';
export const dynamic = 'force-dynamic';

/**
 * if matching url method === 'exact' then pathname should match the URL or array of URLs exactly
 * if matching url method === 'startsWith' then pathname should start with the URL or array of URLs
 * if matching url method === 'oneOf' then pathname should match any of the URLs in the array exactly
 * For navigation URL, if url is array, then the first URL will be used
 *
 * Dynamic segments: `(paramName)` matches one path segment. `(jobId)` and `(id)` match digits only
 * so static siblings like `assessments` / `myJobs` do not falsely select Jobs.
 */
function matchSidebarPath(pattern, pathname, mode = 'exact') {
  // Escape regex metacharacters first (including parens), then turn \(param\) back into segment matchers.
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const withParams = escaped.replace(/\\\(([^/)]+)\\\)/g, (_match, paramName) => {
    if (paramName === 'jobId' || paramName === 'id') {
      return '\\d+';
    }
    return '[^/]+';
  });
  const source = mode === 'startsWith' ? `^${withParams}` : `^${withParams}$`;
  return new RegExp(source).test(pathname);
}
const SIDEBAR_ITEMS = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: RxDashboard,
  url: ({
    companySlug
  }) => `/${companySlug}/`,
  urlMatch: 'exact',
  roles: true,
  testId: 'sidebar/dashboard',
  hidden: true
}, {
  id: 'my-jobs',
  label: 'Jobs',
  icon: TbBriefcase2,
  url: ({
    companySlug
  }) => [`/${companySlug}/recruitment/jobs/myJobs`, `/${companySlug}/recruitment/jobs/new`, `/${companySlug}/recruitment/jobs/edit/(jobId)`, `/${companySlug}/recruitment/jobs/(jobId)`],
  urlMatch: 'oneOf',
  roles: [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentLead],
  testId: 'sidebar/my-jobs',
  hidden: false
}, {
  id: 'talent',
  label: 'Talent',
  icon: TbUsers,
  url: ({
    companySlug
  }) => `/${companySlug}/recruitment/prot/talent`,
  urlMatch: 'startsWith',
  roles: [UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentLead],
  testId: 'sidebar/talent',
  hidden: false
}, {
  id: 'insights',
  label: 'Insights',
  icon: MdOutlineInsights,
  url: ({
    companySlug
  }) => [`/${companySlug}/recruitment/dashboard`, `/${companySlug}/recruitment/reports`, `/${companySlug}/recruitment/reports/ta-tracker`],
  urlMatch: 'startsWith',
  roles: [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentLead],
  testId: 'sidebar/insights',
  hidden: false
}, {
  id: 'job-descriptions',
  label: 'Descriptions',
  icon: LuFileText,
  url: ({
    companySlug
  }) => `/${companySlug}/recruitment/job-descriptions`,
  urlMatch: 'startsWith',
  roles: [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentManager, UserRole.TalentLead],
  testId: 'sidebar/job-descriptions',
  hidden: false
}, {
  id: 'assessments',
  label: 'Assessments',
  icon: MdOutlineChecklistRtl,
  url: ({
    companySlug
  }) => `/${companySlug}/recruitment/jobs/assessments`,
  urlMatch: 'startsWith',
  roles: [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentManager, UserRole.TalentLead],
  testId: 'sidebar/assessments',
  hidden: false
}, {
  id: 'referrals',
  label: 'Referrals',
  icon: TbGift,
  url: ({
    companySlug
  }) => `/${companySlug}/recruitment/prot/referrals`,
  urlMatch: 'startsWith',
  roles: [UserRole.Manager, UserRole.Employee],
  testId: 'sidebar/referrals',
  hidden: true
}, {
  id: 'career',
  label: 'Career',
  icon: LuBuilding2,
  url: ({
    companySlug
  }) => `/${companySlug}/career/jobs`,
  urlMatch: 'startsWith',
  roles: [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentLead],
  testId: 'sidebar/career',
  hidden: false,
  external: true
}, {
  id: 'organizations',
  label: 'Organizations',
  icon: TbBuilding,
  url: _ => '/panel/organizations',
  urlMatch: 'startsWith',
  roles: [UserRole.SuperAdmin],
  testId: 'sidebar/organizations',
  hidden: false
}, {
  id: 'plans',
  label: 'Plans',
  icon: TbCreditCard,
  url: _ => '/panel/plans',
  urlMatch: 'startsWith',
  roles: [UserRole.SuperAdmin],
  testId: 'sidebar/plans',
  hidden: false
}, {
  id: 'credit-costs',
  label: 'Credit costs',
  icon: TbCoins,
  url: _ => '/panel/credit-costs',
  urlMatch: 'startsWith',
  roles: [UserRole.SuperAdmin],
  testId: 'sidebar/credit-costs',
  hidden: false
}, {
  id: 'pending-signups',
  label: 'Pending Signups',
  icon: TbUserPlus,
  url: _ => '/panel/pending-signups',
  urlMatch: 'startsWith',
  roles: [UserRole.SuperAdmin],
  testId: 'sidebar/pending-signups',
  hidden: false
}, {
  id: 'interview-quality',
  label: 'Interview quality',
  icon: TbHeartRateMonitor,
  url: _ => '/panel/interview-quality',
  urlMatch: 'startsWith',
  roles: [UserRole.SuperAdmin],
  testId: 'sidebar/interview-quality',
  hidden: false
}].filter(item => !item.hidden);
const SIDEBAR_FOOTER_ITEMS = [{
  id: 'help-center',
  label: 'Help Center',
  icon: TbHelpCircle,
  url: ({
    companySlug
  }) => `/${companySlug}/help-center`,
  urlMatch: 'startsWith',
  roles: true,
  testId: 'sidebar/help-center',
  hidden: true
}, {
  id: 'settings',
  label: 'Settings',
  icon: TbSettings,
  url: ({
    companySlug
  }) => `/${companySlug}/settings`,
  urlMatch: 'startsWith',
  testId: 'sidebar/settings',
  hidden: false,
  roles: [UserRole.Admin]
}].filter(item => !item.hidden);
const NAV_ACTIVE_GRADIENT = 'linear-gradient(190.98deg, rgba(120, 100, 255, 0.3) -5.08%, rgba(152, 141, 252, 0.5) 10.18%, rgba(169, 218, 247, 0.3) 28.78%, rgba(255, 255, 255, 0.3) 52.15%, rgba(169, 218, 247, 0.3) 74.56%, rgba(122, 102, 255, 0.5) 91.24%)';
function useNavActiveIndicator(selectedId, layoutKey) {
  const listRef = useRef(null);
  const [box, setBox] = useState({
    y: 0,
    height: 0,
    ready: false
  });
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list || !selectedId) {
      setBox(prev => prev.ready ? {
        ...prev,
        ready: false
      } : prev);
      return;
    }
    const el = list.querySelector(`[data-nav-id="${CSS.escape(selectedId)}"]`);
    if (!el) {
      setBox(prev => prev.ready ? {
        ...prev,
        ready: false
      } : prev);
      return;
    }
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const next = {
      y: elRect.top - listRect.top + list.scrollTop,
      height: elRect.height,
      ready: true
    };
    setBox(prev => prev.y === next.y && prev.height === next.height && prev.ready === next.ready ? prev : next);
  }, [selectedId]);
  useLayoutEffect(() => {
    measure();
  }, [measure, layoutKey]);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const resizeObserver = new ResizeObserver(() => {
      // Defer so heavy route paints don't block the indicator frame
      requestAnimationFrame(measure);
    });
    resizeObserver.observe(list);
    list.querySelectorAll('[data-nav-id]').forEach(node => resizeObserver.observe(node));
    return () => resizeObserver.disconnect();
  }, [measure, layoutKey]);
  return {
    listRef,
    box
  };
}
const NavSection = ({
  selectedId,
  layoutKey,
  className,
  children
}) => {
  const {
    listRef,
    box
  } = useNavActiveIndicator(selectedId, layoutKey);
  return <div ref={listRef} className={clsx('relative w-full flex flex-col gap-2', className)}>
            {box.ready ? <motion.div className="pointer-events-none absolute left-0 right-0 top-0 z-0 rounded-lg will-change-transform" aria-hidden initial={false} animate={{
      y: box.y,
      height: box.height,
      opacity: 1
    }} transition={SIDEBAR_MOTION_TRANSITION} style={{
      background: NAV_ACTIVE_GRADIENT
    }}>
                    <div className="absolute inset-[2px] rounded-md bg-page" />
                </motion.div> : null}
            {children}
        </div>;
};
const NavItem = ({
  id,
  label,
  url,
  isSelected,
  isNavExpanded,
  onClick,
  onSelect,
  external = false,
  'data-testid': testId,
  badgeCount,
  activeIndicator = true,
  ...props
}) => {
  const Component = onClick ? 'button' : external ? 'a' : Link;
  return <Tooltip title={label} placement="right" arrow disableHoverListener={isNavExpanded}>
            <Component key={label} data-nav-id={id}
    // @ts-expect-error href can be null
    href={url} style={isSelected && !activeIndicator ? {
      background: NAV_ACTIVE_GRADIENT
    } : undefined}
    // px-2.5: 10 + 20px icon (text-xl) + 10 = the collapsed rail's 40px item, so the icon sits centred.
    className={clsx('relative z-10 w-full flex items-center gap-4 rounded-lg overflow-hidden px-2.5 py-2 cursor-pointer', !isSelected && 'hover:bg-primary/10')} onPointerDown={() => {
      if (activeIndicator) onSelect?.(id);
    }} onClick={onClick} data-testid={testId} {...external ? {
      target: '_blank'
    } : {}}>
                {isSelected && !activeIndicator ? <span className="pointer-events-none absolute inset-[2px] rounded-md bg-page z-0" aria-hidden /> : null}

                {badgeCount && badgeCount > 0 ? <Badge badgeContent={badgeCount} color="primary" sx={{
        '& .MuiBadge-badge': {
          fontSize: 9,
          lineHeight: 1,
          minWidth: 14,
          height: 14,
          padding: '0 3px',
          borderRadius: 999,
          bgcolor: 'var(--color-blue-500)',
          color: '#fff',
          fontWeight: 600,
          right: 0,
          top: 0,
          // MUI centres the badge on the icon's corner; a "99+" pill then reached past the item's
          // 10px padding and the item's overflow-hidden clipped its end. Shift it back inside.
          transform: 'scale(1) translate(35%, -45%)',
          // Above the bell (the icon is z-10, MUI's badge only z-index 1, so the bell painted over it).
          zIndex: 11
        }
      }}>
                        <props.icon className={clsx('text-xl z-10 shrink-0', isSelected ? 'text-primary' : 'text-foreground-muted')} />
                    </Badge> : <props.icon className={clsx('text-xl z-10 shrink-0', isSelected ? 'text-primary' : 'text-foreground-muted')} />}
                <Typography variant="body1" aria-hidden={!isNavExpanded} className={clsx('font-medium! z-10 shrink-0 whitespace-nowrap', isSelected ? 'text-foreground font-medium' : 'text-foreground-muted')}>
                    {label}
                </Typography>
            </Component>
        </Tooltip>;
};
export const AppLayoutSidebar = ({
  isHidden = false
}) => {
  const {
    companySlug
  } = useParams();
  const pathname = usePathname();
  const {
    user,
    roles,
    isUserLoading,
    getUser
  } = useUserContext();
  const {
    isCompanyReadonly
  } = useCompanyContext();
  const {
    isPageSettingEnabled,
    ...userData
  } = user || {};
  const role = getPrimaryAppRole(roles ?? [], DEFAULT_PRIMARY_ROLE_PRIORITY) ?? 'External';
  const hasAttemptedFetch = useRef(false);
  const [expanded, setExpanded] = useState(false);

  // user dropdown menu
  const [anchorEl, setAnchorEl] = useState(null);

  // notifications
  const [unreadCount, setUnreadCount] = useState(0);
  const userMenuOpen = Boolean(anchorEl);

  // notifications panel
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  useEffect(() => {
    const noUser = !user;
    const notLoading = !isUserLoading;
    const notAttempted = !hasAttemptedFetch.current;
    if (noUser && notLoading && notAttempted) {
      hasAttemptedFetch.current = true;
      getUser({
        companySlug
      });
    }
  }, [user, isUserLoading, getUser, companySlug]);
  const isMatchingUrl = (paths, matcherFunction) => {
    if (Array.isArray(paths)) {
      return paths.some(path => matcherFunction(path));
    } else {
      return matcherFunction(paths);
    }
  };
  const selectedItem = useMemo(() => {
    return [...SIDEBAR_ITEMS, ...SIDEBAR_FOOTER_ITEMS].find(item => {
      const res = item.url({
        companySlug
      });
      if (item.urlMatch === 'exact') {
        return isMatchingUrl(res, path => matchSidebarPath(path, pathname, 'exact'));
      } else if (item.urlMatch === 'startsWith') {
        return isMatchingUrl(res, path => matchSidebarPath(path, pathname, 'startsWith'));
      } else if (item.urlMatch === 'oneOf') {
        return isMatchingUrl(res, path => matchSidebarPath(path, pathname, 'exact'));
      }
      return false;
    });
  }, [pathname, companySlug]);

  // Optimistic selection so the indicator moves before heavy route work starts
  const [activeNavId, setActiveNavId] = useState(selectedItem?.id);
  useEffect(() => {
    setActiveNavId(selectedItem?.id);
  }, [selectedItem?.id]);
  const filterSidebarItems = useMemo(() => {
    return SIDEBAR_ITEMS.filter(item => {
      if (item.roles !== true && !(Array.isArray(item.roles) && item.roles.includes(role))) {
        return false;
      }
      return true;
    });
  }, [role]);

  // array of pathToDisableTopNav
  if (!isPageSettingEnabled && checkDisableNav(pathname, pathToDisableTopNav)) {
    return null;
  }
  if (checkDisableNav(pathname, [`/career/screeningInterview`])) {
    return null;
  }
  if (checkDisableNav(pathname, pathToDisableTopNav) && (!userData || Object.keys(userData).length === 0)) {
    return null;
  }
  if (isHidden) {
    return null;
  }
  return <>
            <div className={clsx('flex h-dvh overflow-hidden transition-[width]', SIDEBAR_CSS_TRANSITION,
    // Collapsed rail: 64px, 54px on phones. shrink-0, as the page beside it is w-full and would squeeze it.
    expanded ? 'w-80' : 'w-16 shrink-0 max-md:w-[54px]')}>
                <div className={clsx('flex flex-col items-center justify-start h-full w-full py-4 transition-[padding]', SIDEBAR_CSS_TRANSITION,
      // Collapsed: 40px items with centred icons — 64px rail - 12px each side; 54px - 7px on phones.
      expanded ? 'px-2 sm:px-3 md:px-4' : 'px-3 max-md:px-[7px]')}>
                    <div className="flex w-full h-full flex-col items-center justify-center">
                        <AppLayoutLogo expanded={expanded} onToggleExpanded={() => setExpanded(c => !c)} />
                        {/* Navigation items */}
                        <NavSection selectedId={activeNavId && filterSidebarItems.some(item => item.id === activeNavId) ? activeNavId : undefined} layoutKey={expanded} className="mt-8">
                            {filterSidebarItems.map(item => {
              const urls = item.url({
                companySlug
              });
              return <NavItem key={item.label} id={item.id} label={item.label} icon={item.icon} url={Array.isArray(urls) ? urls[0] : urls} external={item?.external} isSelected={activeNavId === item.id} isNavExpanded={expanded} onSelect={setActiveNavId} data-testid={item.testId} />;
            })}
                        </NavSection>
                        {/* Footer */}
                        <div className="mt-auto" />
                        <AppLayoutUsageBanner expanded={expanded} roles={roles ?? []} className="mb-6" />
                        <NavSection selectedId={activeNavId && SIDEBAR_FOOTER_ITEMS.some(item => item.id === activeNavId) ? activeNavId : undefined} layoutKey={expanded}>
                            {/* Notifications panel */}
                            {Boolean(role) && [UserRole.Manager, UserRole.Recruiter, UserRole.Admin, UserRole.HRHead, UserRole.TalentLead].includes(role) && <NavItem key="notifications" id="notifications" label="Notifications" icon={TbBell} url={null} isSelected={notificationsAnchorEl !== null} isNavExpanded={expanded} activeIndicator={false} onClick={e => setNotificationsAnchorEl(e.currentTarget)} data-testid="sidebar/notifications" badgeCount={unreadCount} />}
                            {SIDEBAR_FOOTER_ITEMS.filter(item => (item.roles === true || Array.isArray(item.roles) && item.roles.includes(role)) && !(item.id === 'settings' && isCompanyReadonly)).map(item => <NavItem key={item.label} id={item.id} label={item.label} icon={item.icon} url={Array.isArray(item.url({
              companySlug
            })) ? item.url({
              companySlug
            })[0] : item.url({
              companySlug
            })} isSelected={activeNavId === item.id} isNavExpanded={expanded} onSelect={setActiveNavId} data-testid={item.testId} />)}
                        </NavSection>

                        <div className={clsx('w-full h-px bg-black/10! dark:bg-white/10! transition-[margin]', SIDEBAR_CSS_TRANSITION, expanded ? 'mt-6 mb-8' : 'mt-4 mb-6')} />

                        {userData && user && Object.keys(userData).length > 0 && <div className="w-full">
                                <ListItemButton onClick={event => {
              setAnchorEl(event.currentTarget);
            }} component="button" data-testid="sidebar/user-menu" className={clsx('cursor-pointer rounded-lg! min-w-0! shrink-0! overflow-hidden! justify-start! items-center!', 'bg-black/5! dark:bg-white/5! hover:bg-black/10! dark:hover:bg-white/10!', 'border! border-black/5! dark:border-white/5!', 'transition-[width,height,padding,background-color]!', SIDEBAR_CSS_TRANSITION_IMPORTANT, expanded ? 'w-full! h-[50px]! p-2!' : 'w-10! h-10! p-[7px]!')} dense>
                                    <ListItemAvatar className="min-w-0! m-0! p-0! shrink-0! flex items-center justify-center">
                                        <Avatar src={user.picture || ''} alt={user?.name || 'John Doe'} className={clsx('transition-[width,height]', SIDEBAR_CSS_TRANSITION, expanded ? 'size-8!' : 'size-6!')} />
                                    </ListItemAvatar>

                                    <div className={clsx('flex flex-1 items-center min-w-0 ml-2.5 overflow-hidden transition-opacity', SIDEBAR_CSS_TRANSITION, expanded ? 'opacity-100' : 'opacity-0')} aria-hidden={!expanded}>
                                        <ListItemText primary={user?.name || 'John Doe'} secondary={user?.email || 'john.doe@example.com'} className="min-w-0! overflow-hidden! flex-1! my-0!" primaryTypographyProps={{
                  noWrap: true,
                  className: 'text-foreground! font-medium!'
                }} secondaryTypographyProps={{
                  noWrap: true,
                  className: 'text-foreground-muted! text-[11px]!'
                }} />
                                        <FiChevronRight className="ml-1! size-4 shrink-0 text-foreground-muted!" />
                                    </div>
                                </ListItemButton>

                                <AppLayoutUserMenu anchorEl={anchorEl} open={userMenuOpen} onClose={() => setAnchorEl(null)} />
                            </div>}
                    </div>
                </div>
            </div>

            <NotificationsPanel isOpen={notificationsOpen} onClose={() => setNotificationsAnchorEl(null)} anchorEl={notificationsAnchorEl} onCountChange={setUnreadCount} />
        </>;
};